import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useNotification } from '@context/NotificationContext'
import ApiService from '@services/api'

function normalizeSystemSettings(settings) {
  return {
    TriggerLegalManagerAtivo: Boolean(settings?.TriggerLegalManagerAtivo ?? settings?.triggerLegalManagerAtivo ?? true),
    TriggerLegalManagerIntervaloMinutos: Number(settings?.TriggerLegalManagerIntervaloMinutos ?? settings?.triggerLegalManagerIntervaloMinutos ?? 30),
    TriggerPublicacoesAtivo: Boolean(settings?.TriggerPublicacoesAtivo ?? settings?.triggerPublicacoesAtivo ?? true),
    TriggerPublicacoesIntervaloMinutos: Number(settings?.TriggerPublicacoesIntervaloMinutos ?? settings?.triggerPublicacoesIntervaloMinutos ?? 1440),
    TriggerPublicacoesSchedulerEnabled: Boolean(settings?.TriggerPublicacoesSchedulerEnabled ?? settings?.triggerPublicacoesSchedulerEnabled ?? true),
    TriggerPublicacoesCron: settings?.TriggerPublicacoesCron ?? settings?.triggerPublicacoesCron ?? '0 8 * * *',
    TriggerPublicacoesTimeZoneId: settings?.TriggerPublicacoesTimeZoneId ?? settings?.triggerPublicacoesTimeZoneId ?? 'America/Sao_Paulo',
    TriggerPublicacoesLookbackDays: Number(settings?.TriggerPublicacoesLookbackDays ?? settings?.triggerPublicacoesLookbackDays ?? 1),
    TriggerPublicacoesMaxCatchUpDays: Number(settings?.TriggerPublicacoesMaxCatchUpDays ?? settings?.triggerPublicacoesMaxCatchUpDays ?? 30),
    TriggerPublicacoesPollIntervalSeconds: Number(settings?.TriggerPublicacoesPollIntervalSeconds ?? settings?.triggerPublicacoesPollIntervalSeconds ?? 60),
    TriggerPublicacoesMaxAttemptsPerDay: Number(settings?.TriggerPublicacoesMaxAttemptsPerDay ?? settings?.triggerPublicacoesMaxAttemptsPerDay ?? 3),
    TriggerPublicacoesRetryDelayMinutes: Number(settings?.TriggerPublicacoesRetryDelayMinutes ?? settings?.triggerPublicacoesRetryDelayMinutes ?? 15),
    TriggerPublicacoesStaleExecutionMinutes: Number(settings?.TriggerPublicacoesStaleExecutionMinutes ?? settings?.triggerPublicacoesStaleExecutionMinutes ?? 60),
    TokenPjeUnificadoAtivo: Boolean(settings?.TokenPjeUnificadoAtivo ?? settings?.tokenPjeUnificadoAtivo ?? true),
    CredencialPjeUnificadaId: settings?.CredencialPjeUnificadaId ?? settings?.credencialPjeUnificadaId ?? null,
    Ativo: Boolean(settings?.Ativo ?? settings?.ativo ?? true),
  }
}

function getPublicationSchedulerSettings(settings) {
  return {
    TriggerPublicacoesSchedulerEnabled: settings.TriggerPublicacoesSchedulerEnabled,
    TriggerPublicacoesCron: settings.TriggerPublicacoesCron,
    TriggerPublicacoesTimeZoneId: settings.TriggerPublicacoesTimeZoneId,
    TriggerPublicacoesLookbackDays: settings.TriggerPublicacoesLookbackDays,
    TriggerPublicacoesMaxCatchUpDays: settings.TriggerPublicacoesMaxCatchUpDays,
    TriggerPublicacoesPollIntervalSeconds: settings.TriggerPublicacoesPollIntervalSeconds,
    TriggerPublicacoesMaxAttemptsPerDay: settings.TriggerPublicacoesMaxAttemptsPerDay,
    TriggerPublicacoesRetryDelayMinutes: settings.TriggerPublicacoesRetryDelayMinutes,
    TriggerPublicacoesStaleExecutionMinutes: settings.TriggerPublicacoesStaleExecutionMinutes,
  }
}

function Field({ label, hint, children, inline = false }) {
  return (
    <label className={`rules-field ${inline ? 'is-inline' : ''}`}>
      <span className="rules-field-label">{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  )
}

function SectionCard({ title, subtitle, actions, children }) {
  return (
    <section className="rules-card">
      <div className="rules-card-header">
        <div>
          <h3>{title}</h3>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {actions && <div className="rules-card-actions">{actions}</div>}
      </div>
      <div className="rules-card-body">{children}</div>
    </section>
  )
}

export default function SystemSettingsPage() {
  const notify = useNotification()
  const [settings, setSettings] = useState()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [publicationSchedulerSaving, setPublicationSchedulerSaving] = useState(false)
  const [publicationScheduler, setPublicationScheduler] = useState(() =>
    getPublicationSchedulerSettings(normalizeSystemSettings({})))
  const [credenciaisPje, setCredenciaisPje] = useState([])
  const [usuarios, setUsuarios] = useState([])
  const [tokenUsuarioId, setTokenUsuarioId] = useState('')
  const [tokenTribunal, setTokenTribunal] = useState('PJe')
  const [tokenChaveSecreta, setTokenChaveSecreta] = useState('')
  const [tokenSaving, setTokenSaving] = useState(false)
  const [aiValidationEnabled, setAiValidationEnabled] = useState(false)
  const [aiValidationSamplingPercent, setAiValidationSamplingPercent] = useState(10)
  const [aiValidationSaving, setAiValidationSaving] = useState(false)

  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true)
      const [response, credenciaisResponse, usuariosResponse, aiValidationResponse] = await Promise.all([
        ApiService.getSystemSettings(),
        ApiService.getCredenciaisPje(),
        ApiService.getUsuarios(1000, 1),
        ApiService.getDeadlineAiValidationRuntime(),
      ])
      setLoading(false)

      if (!response.success) {
        notify.showError(response.error || 'Não foi possível carregar as configurações do sistema.')
        return
      }

      const normalizedSettings = normalizeSystemSettings(response.data)
      setSettings(normalizedSettings)
      setPublicationScheduler(getPublicationSchedulerSettings(normalizedSettings))
      if (credenciaisResponse.success) {
        setCredenciaisPje(credenciaisResponse.items || credenciaisResponse.data || [])
      }
      if (usuariosResponse.success) {
        setUsuarios(usuariosResponse.items || usuariosResponse.data || [])
      }
      if (aiValidationResponse.success) {
        setAiValidationEnabled(Boolean(aiValidationResponse.data?.enabled ?? aiValidationResponse.data?.Enabled))
        const samplingRate = Number(aiValidationResponse.data?.samplingRate ?? aiValidationResponse.data?.SamplingRate ?? 0.10)
        setAiValidationSamplingPercent(Math.round(samplingRate * 100))
      }
    }

    loadSettings()
  }, [notify])

  const handleSystemSettingChange = async (key, value) => {
    const previousSettings = settings
    const nextSettings = { ...settings, [key]: value }
    setSettings(nextSettings)
    setSaving(true)

    const response = await ApiService.updateSystemSettings(nextSettings)
    setSaving(false)

    if (!response.success) {
      setSettings(previousSettings)
      notify.showError(response.error || 'Não foi possível salvar as configurações do sistema.')
      return
    }

    if (response.data) {
      setSettings(normalizeSystemSettings(response.data))
    }
  }

  const handlePublicationSchedulerSubmit = async (event) => {
    event.preventDefault()
    setPublicationSchedulerSaving(true)
    const response = await ApiService.updateSystemSettings({ ...settings, ...publicationScheduler })
    setPublicationSchedulerSaving(false)

    if (!response.success) {
      notify.showError(response.error || 'Não foi possível salvar a agenda de publicações.')
      return
    }

    const normalizedSettings = normalizeSystemSettings(response.data)
    setSettings(normalizedSettings)
    setPublicationScheduler(getPublicationSchedulerSettings(normalizedSettings))
    notify.showSuccess('Agenda de publicações atualizada.')
  }

  const setPublicationSchedulerField = (key, value) => {
    setPublicationScheduler((current) => ({ ...current, [key]: value }))
  }

  const saveAiValidationRuntime = async (enabled, samplingPercent) => {
    const normalizedPercent = Math.min(100, Math.max(0, Number(samplingPercent)))
    if (!Number.isFinite(normalizedPercent)) {
      notify.showError('Informe uma amostragem entre 0% e 100%.')
      return false
    }
    setAiValidationSaving(true)
    const response = await ApiService.updateDeadlineAiValidationRuntime(enabled, normalizedPercent / 100)
    setAiValidationSaving(false)
    if (!response.success) {
      notify.showError(response.error || 'Não foi possível atualizar a validação por IA.')
      return false
    }
    setAiValidationEnabled(Boolean(response.data?.enabled ?? response.data?.Enabled ?? enabled))
    setAiValidationSamplingPercent(Math.round(Number(response.data?.samplingRate ?? response.data?.SamplingRate ?? normalizedPercent / 100) * 100))
    return true
  }

  const handleAiValidationChange = async (enabled) => {
    const previous = aiValidationEnabled
    setAiValidationEnabled(enabled)
    const saved = await saveAiValidationRuntime(enabled, aiValidationSamplingPercent)
    if (!saved) {
      setAiValidationEnabled(previous)
      return
    }
    notify.showSuccess(enabled ? 'Revisão da análise por IA ativada.' : 'Revisão por IA desativada.')
  }

  const handleAiValidationSamplingBlur = async () => {
    const previous = aiValidationSamplingPercent
    const normalizedPercent = Math.min(100, Math.max(0, Number(aiValidationSamplingPercent)))
    setAiValidationSamplingPercent(normalizedPercent)
    const saved = await saveAiValidationRuntime(aiValidationEnabled, normalizedPercent)
    if (!saved) setAiValidationSamplingPercent(previous)
    else notify.showSuccess(`Amostragem da validação por IA ajustada para ${normalizedPercent}%.`)
  }

  const handleCadastrarToken = async (event) => {
    event.preventDefault()
    setTokenSaving(true)

    const cadastroResponse = await ApiService.cadastrarCredencialPje({
      usuarioId: Number(tokenUsuarioId),
      tribunal: tokenTribunal.trim(),
      chaveSecreta: tokenChaveSecreta.trim(),
    })

    if (!cadastroResponse.success) {
      setTokenSaving(false)
      notify.showError(cadastroResponse.error || 'Não foi possível cadastrar a credencial 2FA.')
      return
    }

    const credencial = cadastroResponse.data
    const nextSettings = {
      ...settings,
      TokenPjeUnificadoAtivo: true,
      CredencialPjeUnificadaId: credencial.id,
    }
    const settingsResponse = await ApiService.updateSystemSettings(nextSettings)
    setTokenSaving(false)

    if (!settingsResponse.success) {
      notify.showError(settingsResponse.error || 'A credencial foi salva, mas não pôde ser definida como principal.')
      return
    }

    setSettings(normalizeSystemSettings(settingsResponse.data))
    setCredenciaisPje((current) => [
      ...current.filter((item) => item.id !== credencial.id),
      credencial,
    ].sort((left, right) => left.nomeAdvogado.localeCompare(right.nomeAdvogado)))
    setTokenChaveSecreta('')
    notify.showSuccess('Credencial cadastrada e definida como token principal.')
  }

  if (!settings) {
    return <div className="main-content rules-config-page">Carregando configurações...</div>
  }

  return (
    <div className="main-content rules-config-page">
      <div className="page-header rules-page-header">
        <div>
          {loading && <small>Sincronizando dados com o backend...</small>}
          {saving && <small>Salvando configurações do sistema...</small>}
        </div>
      </div>

      <div className="rules-layout-grid">
        <SectionCard title="Parâmetros técnicos" subtitle="Ative ou pause as rotinas automáticas e configure o Legal Manager.">
          <div className="rules-form-grid two-columns">
            <Field label="Trigger Legal Manager ativa" inline>
              <input
                type="checkbox"
                checked={settings.TriggerLegalManagerAtivo}
                onChange={(event) => handleSystemSettingChange('TriggerLegalManagerAtivo', event.target.checked)}
              />
            </Field>
            <Field label="Intervalo Legal Manager (min)">
              <input
                type="number"
                min="1"
                max="1440"
                value={settings.TriggerLegalManagerIntervaloMinutos}
                onChange={(event) => handleSystemSettingChange('TriggerLegalManagerIntervaloMinutos', Number(event.target.value))}
              />
            </Field>
            <Field label="Monitoramento de publicações ativo" inline>
              <input
                type="checkbox"
                checked={settings.TriggerPublicacoesAtivo}
                disabled={!settings.TriggerPublicacoesSchedulerEnabled}
                onChange={(event) => handleSystemSettingChange('TriggerPublicacoesAtivo', event.target.checked)}
              />
            </Field>
          </div>
        </SectionCard>

        <SectionCard
          title="Agenda de publicações"
          subtitle="Configure a execução automática do monitoramento. As alterações passam a valer sem reiniciar o backend."
        >
          <form onSubmit={handlePublicationSchedulerSubmit}>
            <div className="rules-form-grid two-columns">
              <Field label="Scheduler habilitado" hint="Desliga somente o agendador automático; execuções manuais continuam disponíveis." inline>
                <input
                  type="checkbox"
                  checked={publicationScheduler.TriggerPublicacoesSchedulerEnabled}
                  onChange={(event) => setPublicationSchedulerField('TriggerPublicacoesSchedulerEnabled', event.target.checked)}
                />
              </Field>
              <Field label="Expressão cron" hint="Use cinco campos. Exemplo: 0 8 * * * executa diariamente às 08:00.">
                <input
                  type="text"
                  value={publicationScheduler.TriggerPublicacoesCron}
                  onChange={(event) => setPublicationSchedulerField('TriggerPublicacoesCron', event.target.value)}
                  maxLength="100"
                  required
                />
              </Field>
              <Field label="Fuso horário" hint="Identificador IANA usado para interpretar a agenda.">
                <input
                  type="text"
                  value={publicationScheduler.TriggerPublicacoesTimeZoneId}
                  onChange={(event) => setPublicationSchedulerField('TriggerPublicacoesTimeZoneId', event.target.value)}
                  placeholder="America/Sao_Paulo"
                  maxLength="100"
                  required
                />
              </Field>
              <Field label="Janela retroativa (dias)" hint="Inclui dias anteriores em uma execução normal.">
                <input
                  type="number"
                  min="0"
                  max="7"
                  value={publicationScheduler.TriggerPublicacoesLookbackDays}
                  onChange={(event) => setPublicationSchedulerField('TriggerPublicacoesLookbackDays', Number(event.target.value))}
                  required
                />
              </Field>
              <Field label="Máximo de recuperação (dias)" hint="Limita o catch-up após indisponibilidade.">
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={publicationScheduler.TriggerPublicacoesMaxCatchUpDays}
                  onChange={(event) => setPublicationSchedulerField('TriggerPublicacoesMaxCatchUpDays', Number(event.target.value))}
                  required
                />
              </Field>
              <Field label="Intervalo de verificação (segundos)" hint="Frequência com que o backend verifica se a agenda venceu.">
                <input
                  type="number"
                  min="15"
                  max="300"
                  value={publicationScheduler.TriggerPublicacoesPollIntervalSeconds}
                  onChange={(event) => setPublicationSchedulerField('TriggerPublicacoesPollIntervalSeconds', Number(event.target.value))}
                  required
                />
              </Field>
              <Field label="Tentativas por dia" hint="Limite de novas tentativas para cada cliente e data agendada.">
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={publicationScheduler.TriggerPublicacoesMaxAttemptsPerDay}
                  onChange={(event) => setPublicationSchedulerField('TriggerPublicacoesMaxAttemptsPerDay', Number(event.target.value))}
                  required
                />
              </Field>
              <Field label="Espera para nova tentativa (minutos)" hint="Cooldown aplicado depois de erro ou cancelamento.">
                <input
                  type="number"
                  min="0"
                  max="1440"
                  value={publicationScheduler.TriggerPublicacoesRetryDelayMinutes}
                  onChange={(event) => setPublicationSchedulerField('TriggerPublicacoesRetryDelayMinutes', Number(event.target.value))}
                  required
                />
              </Field>
              <Field label="Execução obsoleta após (minutos)" hint="Permite recuperar execuções que ficaram presas em andamento.">
                <input
                  type="number"
                  min="1"
                  max="1440"
                  value={publicationScheduler.TriggerPublicacoesStaleExecutionMinutes}
                  onChange={(event) => setPublicationSchedulerField('TriggerPublicacoesStaleExecutionMinutes', Number(event.target.value))}
                  required
                />
              </Field>
            </div>
            <button type="submit" className="btn btn-primary" disabled={publicationSchedulerSaving}>
              <i className={`fas ${publicationSchedulerSaving ? 'fa-spinner fa-spin' : 'fa-save'}`} />
              {publicationSchedulerSaving ? ' Salvando...' : ' Salvar agenda de publicações'}
            </button>
          </form>
        </SectionCard>

        <SectionCard title="Resumo do estado" subtitle="Visão rápida da operação atual do scheduler.">
          <div className="summary-stack">
            <div className="summary-item">
              <strong>Legal Manager</strong>
              <span>{settings.TriggerLegalManagerAtivo ? 'Ativo' : 'Pausado'}</span>
            </div>
            <div className="summary-item">
              <strong>Intervalo Legal Manager</strong>
              <span>{settings.TriggerLegalManagerIntervaloMinutos} min</span>
            </div>
            <div className="summary-item">
              <strong>Publicações</strong>
              <span>{settings.TriggerPublicacoesSchedulerEnabled && settings.TriggerPublicacoesAtivo ? 'Ativo' : 'Pausado'}</span>
            </div>
            <div className="summary-item">
              <strong>Agenda Publicações</strong>
              <span>{settings.TriggerPublicacoesCron} · {settings.TriggerPublicacoesTimeZoneId}</span>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Revisão por IA" subtitle="Controle exclusivo de super administrador. A IA recebe a análise concluída do motor e sugere correções para conferência humana.">
          <Field
            label="Ativar integração de validação por IA"
            hint="Quando ativa, uma amostra das análises concluídas é revisada pelo provedor Gemini, OpenAI ou Anthropic configurado no serviço."
            inline
          >
            <input
              type="checkbox"
              checked={aiValidationEnabled}
              disabled={aiValidationSaving}
              onChange={(event) => handleAiValidationChange(event.target.checked)}
            />
          </Field>
          <Field
            label="Amostragem de simulações para IA"
            hint="Percentual de análises elegíveis enviadas para revisão por IA. 0% pausa as chamadas; 100% seleciona todas."
          >
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={aiValidationSamplingPercent}
              disabled={aiValidationSaving}
              onChange={(event) => setAiValidationSamplingPercent(event.target.value)}
              onBlur={handleAiValidationSamplingBlur}
            />
          </Field>
          <p className="rules-inline-note">Estado atual: {aiValidationEnabled ? 'Revisão por IA ativa' : 'Desativada'}.</p>
        </SectionCard>

        <SectionCard title="Base jurídica" subtitle="Configure as peças do escritório e as referências utilizadas pela IA.">
          <Link to="/base-juridica/configuracoes?secao=referencias" className="btn btn-outline">
            <i className="fas fa-book-open" aria-hidden="true" /> Configurar base jurídica
          </Link>
        </SectionCard>

        <SectionCard
          title="Token PJe unificado"
          subtitle="Cadastre a chave com segurança e compartilhe o código 2FA principal com todos os usuários."
        >
          <form className="pje-admin-form" onSubmit={handleCadastrarToken}>
            <Field label="Usuário proprietário" hint="Pode ser administrador, advogado ou qualquer outro perfil ativo.">
              <select value={tokenUsuarioId} onChange={(event) => setTokenUsuarioId(event.target.value)} required>
                <option value="">Selecione o usuário</option>
                {usuarios.filter((usuario) => usuario.ativo !== false).map((usuario) => (
                  <option key={usuario.id} value={usuario.id}>
                    {usuario.nome} — {usuario.role}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Tribunal ou portal">
              <input
                value={tokenTribunal}
                onChange={(event) => setTokenTribunal(event.target.value)}
                placeholder="Ex.: PJe TRT-2"
                maxLength={100}
                required
              />
            </Field>
            <Field label="Chave secreta Base32" hint="Será criptografada e não poderá ser consultada depois.">
              <input
                type="password"
                value={tokenChaveSecreta}
                onChange={(event) => setTokenChaveSecreta(event.target.value)}
                placeholder="Cole a chave fornecida pelo tribunal"
                autoComplete="off"
                required
              />
            </Field>
            <button type="submit" className="btn btn-primary" disabled={tokenSaving}>
              <i className={`fas ${tokenSaving ? 'fa-spinner fa-spin' : 'fa-lock'}`} />
              {tokenSaving ? ' Salvando...' : ' Cadastrar e usar como principal'}
            </button>
          </form>
          <div className="rules-form-grid two-columns">
            <Field
              label="Ativar token unificado"
              hint="Quando ativo, as credenciais individuais deixam de ser usadas para exibição."
              inline
            >
              <input
                type="checkbox"
                checked={settings.TokenPjeUnificadoAtivo}
                disabled={(!settings.CredencialPjeUnificadaId && !settings.TokenPjeUnificadoAtivo) || saving}
                onChange={(event) => handleSystemSettingChange('TokenPjeUnificadoAtivo', event.target.checked)}
              />
            </Field>
            <Field label="Credencial principal" hint="Somente o código desta credencial será exibido.">
              <select
                value={settings.CredencialPjeUnificadaId || ''}
                disabled={saving}
                onChange={(event) => handleSystemSettingChange(
                  'CredencialPjeUnificadaId',
                  event.target.value ? Number(event.target.value) : null
                )}
              >
                <option value="">Selecione uma credencial</option>
                {credenciaisPje.map((credencial) => (
                  <option key={credencial.id} value={credencial.id}>
                    {credencial.nomeAdvogado} — {credencial.tribunal}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {!credenciaisPje.length ? (
            <p className="rules-inline-note">Cadastre ao menos uma credencial PJe antes de ativar o modo unificado.</p>
          ) : null}
        </SectionCard>
      </div>
    </div>
  )
}
